/**
 * Hour at which a cinema "day" starts. Screenings that begin before this hour
 * (e.g. 00:30) belong to the programme of the previous calendar day.
 */
export const CINEMA_DAY_START_HOUR = 3;

/**
 * Returns the cinema day (local midnight) a screening belongs to.
 * A screening starting before `CINEMA_DAY_START_HOUR` is assigned to the previous calendar day.
 */
export const getCinemaDay = (start: Date | string | number): Date => {
  const startDate = new Date(start);
  const day = new Date(startDate);
  day.setHours(0, 0, 0, 0);

  const dayStart = new Date(day);
  dayStart.setHours(CINEMA_DAY_START_HOUR, 0, 0, 0);

  if (startDate < dayStart) {
    day.setDate(day.getDate() - 1);
  }

  return day;
};

/**
 * Normalizes an editor-entered calendar date (e.g. a CMS date field, ISO string at midnight)
 * to local midnight WITHOUT the cinema-day rollback – use `getCinemaDay` for screening times instead.
 */
export const toCalendarDay = (date: Date | string | number): Date => {
  if (typeof date === "string") {
    // take the calendar date from the string itself so the result does not depend on the
    // runtime timezone (e.g. "2026-10-06T00:00:00+02:00" on a UTC server would become 5.10.)
    const match = date.match(/^(\d{4})-(\d{2})-(\d{2})/);
    if (match) {
      return new Date(+match[1], +match[2] - 1, +match[3], 0, 0, 0, 0);
    }
  }
  const day = new Date(date);
  day.setHours(0, 0, 0, 0);
  return day;
};

/**
 * Inclusive interval of screening start times that belong to the given cinema day:
 * from `CINEMA_DAY_START_HOUR` of that day up to (but excluding) `CINEMA_DAY_START_HOUR` of the next day.
 * Suitable for date-fns `isWithinInterval`, which is inclusive on both ends.
 */
export const getCinemaDayInterval = (
  day: Date | number,
): { start: Date; end: Date } => {
  const start = new Date(day);
  start.setHours(CINEMA_DAY_START_HOUR, 0, 0, 0);

  // Resolve the next day's start hour in local time first, then step back 1 ms in epoch time.
  // Subtracting inside setHours would land in the DST-ambiguous hour and create a gap/overlap between days.
  const end = new Date(day);
  end.setHours(CINEMA_DAY_START_HOUR + 24, 0, 0, 0);
  end.setTime(end.getTime() - 1);

  return { start, end };
};

/**
 * Craft query argument for the `Start` field selecting screenings that belong to the cinema days
 * from `firstDay` up to and including `lastDay` (both are cinema days, i.e. local midnight dates).
 * Without `lastDay` the window is open-ended.
 * Filtering by `Start` (not `Finish`) keeps late screenings that end after midnight.
 */
export const getScreeningsStartQueryArgument = (
  firstDay: Date | number,
  lastDay?: Date | number | null,
): string | string[] => {
  const from = `>=${getCinemaDayInterval(firstDay).start.toISOString()}`;
  if (lastDay === undefined || lastDay === null) {
    return from;
  }
  return ["and", from, `<=${getCinemaDayInterval(lastDay).end.toISOString()}`];
};
